import classNames from "classnames";

import { translate } from "@ascendnow/shared";

import { AdminArrowLeftIcon, AdminArrowRightIcon } from "../AscIcons";
import styles from "./AscAdminPagination.module.scss";
import { useRouter } from "next/router";
import AscDropdown from "../AscDropdown/AscDropdown";

export interface AscAdminPaginationProps {
  className?: string;
  pageCount: number;
  page?: number;
  goNext?: () => void;
  goBack?: () => void;
  disablePrev?: boolean;
  disableNext?: boolean;
  showPerPage?: boolean;
}

export default function AscAdminPagination({
  className,
  pageCount,
  page: currentPage,
  goNext,
  goBack,
  disableNext = false,
  disablePrev = false,
  showPerPage = false,
}: AscAdminPaginationProps) {
  const router = useRouter();
  const _page = Number(router.query.page || 1);
  const perPage = Number(router.query.perPage || 25);

  const page = currentPage ?? _page;

  const handleGoNextPage = () => {
    if (page < pageCount && !disableNext) {
      if (goNext) {
        goNext();
      } else {
        router.push(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              page: page + 1,
            },
          },
          undefined,
          { shallow: true }
        );
      }
    }
  };

  const handleGoPrevPage = () => {
    if (perPage > 1) {
      if (goBack) {
        goBack();
      } else {
        router.push(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              page: page - 1,
            },
          },
          undefined,
          { shallow: true }
        );
      }
    }
  };

  const handleClickPerPage = (selectedPerPage: number) => {
    if (perPage !== selectedPerPage) {
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            page: 1,
            perPage: selectedPerPage,
          },
        },
        undefined,
        { shallow: true }
      );
    }
  };

  return (
    <div
      data-testid="asc-admin-pagination"
      className={classNames(styles.ground, className)}
    >
      {showPerPage && (
        <AscDropdown
          title={perPage + " per page"}
          variant="secondary"
          alignment="top-left"
          className={styles.dropdown}
        >
          {[10, 25, 50, 75, 100].map((el, index) => (
            <AscDropdown.Option
              key={index}
              onClick={() => handleClickPerPage(el)}
              active={perPage === el}
              linkClassName={styles.perPageOptionLink}
            >
              {el}
              {translate("perPage:lowercase")}
            </AscDropdown.Option>
          ))}
        </AscDropdown>
      )}
      <div className={styles.pagination}>
        <div
          className={styles.field}
          onClick={handleGoPrevPage}
          aria-disabled={page === 1 || disablePrev}
        >
          <AdminArrowLeftIcon
            className={classNames(
              styles.leftIcon,
              styles.active,
              (page === 1 || disablePrev) && styles.noActive
            )}
          />

          {page !== 1 && (
            <span className={styles.text}>{translate("prev")}</span>
          )}
        </div>
        <div className={styles.pageNumber}>
          <span>{page}</span>
        </div>
        <div
          className={styles.field}
          onClick={handleGoNextPage}
          aria-disabled={page >= pageCount || disableNext}
        >
          {page < pageCount && (
            <span className={styles.text}>{translate("next")}</span>
          )}

          <AdminArrowRightIcon
            className={classNames(
              styles.rightIcon,
              (page >= pageCount || disableNext) && styles.noActive
            )}
          />
        </div>
      </div>
    </div>
  );
}
